import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle, ExternalLink, Copy } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import gamilIcon from "../../assets/images/gmail.webp";
import microsoftIcon from "../../assets/images/outlook.webp";
import yahooIcon from "../../assets/images/yahoopng.png";
import zohoIcon from "../../assets/images/zohomail.png";

export default function AppPasswordStepsModal({ open, onClose }) {
  const [copiedStep, setCopiedStep] = useState(null);

  const copyToClipboard = (text, stepId) => {
    navigator.clipboard.writeText(text);
    setCopiedStep(stepId);
    setTimeout(() => setCopiedStep(null), 2000);
  };

  const providers = [
    {
      id: "gmail",
      name: "Gmail",
      icon: gamilIcon,
      steps: [
        {
          title: "Enable 2-Step Verification",
          content:
            "Open myaccount.google.com and sign in. Go to Security (left menu) → under 'How you sign in to Google' click 2-Step Verification → Get started. Follow the prompts to add a verification method (phone number, authenticator app, or security key).",
          link: "https://myaccount.google.com",
        },
        {
          title: "Create an App Password",
          content:
            "Back in myaccount.google.com → Security → find 'Signing in to Google' → click App passwords. From the drop-downs choose the app (or choose 'Other' and name it) and the device, then click Generate. Copy the 16-character app password.",
          notes: [
            "If you don't see 'App passwords': account uses security keys only, is a work/school account with restrictions, or is in Advanced Protection program.",
          ],
          link: "https://support.google.com/accounts/answer/185833",
        },
      ],
    },
    {
      id: "microsoft",
      name: "Outlook",
      icon: microsoftIcon,
      steps: [
        {
          title: "Turn on two-step verification",
          content:
            "Sign in to your Microsoft account at account.microsoft.com and go to Security. Choose Manage how I sign in or go to Additional security → Two-step verification → Turn on. Follow the on-screen setup.",
          link: "https://account.microsoft.com",
        },
        {
          title: "Create an App Password",
          content:
            "After two-step is enabled, go to Security info or More security settings. Look for App passwords and follow prompts to generate one. Use the generated password in the app.",
          link: "https://support.microsoft.com/help/12409",
        },
      ],
    },
    {
      id: "yahoo",
      name: "Yahoo Mail",
      icon: yahooIcon,
      steps: [
        {
          title: "Enable 2-Step Verification",
          content:
            "Sign in to Yahoo Account Security page. Under 'Ways of signing in' find 2-step verification and turn it on. Choose SMS, authenticator app, or a security key.",
          link: "https://login.yahoo.com/account/security",
        },
        {
          title: "Create an App Password",
          content:
            "From the same Account Security page, find Create app password (under External connections or app passwords section). Enter a name for the app, click Generate and copy the password.",
          link: "https://help.yahoo.com/kb/generate-third-party-passwords-sln15241.html",
        },
      ],
    },
    {
      id: "zoho",
      name: "Zoho Mail",
      icon: zohoIcon,
      steps: [
        {
          title: "Enable 2-Factor Authentication",
          content:
            "Sign in to Zoho Accounts (accounts.zoho.com) and go to Security → Multi-Factor Authentication. Under Time-based OTP click Setup Now, scan the QR code with Google Authenticator / Authy, then confirm with the code.",
          link: "https://accounts.zoho.com",
        },
        {
          title: "Generate App Password",
          content:
            "In Zoho Accounts go to Security → App passwords. Click Generate New Password, give the app a name, click Generate, then copy the password (it won't be shown again).",
          link: "https://www.zoho.com/mail/help/zoho-2step-verification.html",
        },
      ],
    },
  ];

  const quickTips = [
    "Enable 2-Step first — app passwords are only available once 2-step verification is active",
    "Use an authenticator app for stronger security than SMS where possible",
    "Name app passwords clearly (e.g., 'iPhone Mail app — Aug 2025') so you can revoke later",
    "Store recovery codes in a safe place — they help if you lose your phone",
    "If options don't appear: account might be managed by organization or under special security settings",
  ];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="
      w-[calc(100vw-1rem)]
      sm:w-[calc(100vw-3rem)]
      max-w-[720px]
      p-0
      backdrop-blur-sm
    "
      >
        <div className="flex flex-col max-h-[90vh]">
          {/* HEADER */}
          <div className="p-4 sm:p-6 pb-2">
            <DialogHeader>
              <DialogTitle className="text-xl">
                Email Provider Setup Guide
              </DialogTitle>
              <DialogDescription>
                Follow these steps to enable app passwords for your email
                provider
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* SCROLLABLE CONTENT */}
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-6">
            {/* Tabs */}
            <Tabs defaultValue="gmail" className="w-full">
              <TabsList className="grid grid-cols-4 mb-6">
                {providers.map((provider) => (
                  <TabsTrigger
                    key={provider.id}
                    value={provider.id}
                    className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r from-blue-500 to-indigo-600 data-[state=active]:text-white"
                  >
                    <img src={provider.icon} className="h-4" />
                    <span className="hidden sm:inline">{provider.name}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {providers.map((provider) => (
                <TabsContent
                  key={provider.id}
                  value={provider.id}
                  className="space-y-4"
                >
                  <div className="space-y-6">
                    {provider.steps.map((step, index) => (
                      <div
                        key={index}
                        className="border rounded-lg p-4 bg-gray-50/50"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-6 h-6 bg-blue-600 text-white text-sm font-medium rounded-full">
                              {index + 1}
                            </div>
                            <h3 className="font-semibold text-base">
                              {step.title}
                            </h3>
                          </div>

                          {step.link && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2"
                              onClick={() => window.open(step.link, "_blank")}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Guide
                            </Button>
                          )}
                        </div>

                        <p className="text-sm text-gray-600 mb-3 leading-relaxed">
                          {step.content}
                        </p>

                        {step.notes && (
                          <div className="bg-yellow-50 rounded-md border border-yellow-200 p-3">
                            <p className="text-xs font-medium text-yellow-800 mb-1">
                              Note:
                            </p>
                            {step.notes.map((note, noteIndex) => (
                              <p
                                key={noteIndex}
                                className="text-xs text-yellow-700"
                              >
                                {note}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>

            {/* Quick Tips */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                Quick Checklist & Practical Tips
              </h4>

              <div className="space-y-2">
                {quickTips.map((tip, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 text-sm text-gray-600"
                  >
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0" />
                    <span>{tip}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="p-4 sm:p-6 pt-2 border-t flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button onClick={onClose} className="main-button">
              I'm Ready to Connect
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
